import type { EmptyEncoding, FieldType, Issue, RecordNode } from '@resource-kit/model';
import { fieldValuePolicy } from './field-value-policy';

/**
 * What the payload builder needs to know about one form field (#1797).
 * `encodeEmpty` is the per-endpoint override described in
 * `field-value-policy.ts`; absent ⇒ the field type's repo default.
 */
export interface FormFieldSpec {
  key: string;
  type: FieldType;
  encodeEmpty?: EmptyEncoding;
}

type Draft<T> = Partial<T>;
type Keys<T> = ReadonlySet<keyof T>;

const asRecord = (value: unknown): Record<string, unknown> =>
  (value ?? {}) as Record<string, unknown>;

const withKey = <T>(set: Keys<T>, key: keyof T): ReadonlySet<keyof T> => new Set([...set, key]);

const withoutKey = <T>(set: Keys<T>, key: keyof T): ReadonlySet<keyof T> => {
  const next = new Set(set);
  next.delete(key);
  return next;
};

export function createRecordNode<T>(args: {
  id: string | null;
  initial: T | null;
  draft?: Draft<T>;
}): RecordNode<T> {
  return {
    id: args.id,
    initial: args.initial,
    draft: { ...args.draft },
    touched: new Set<keyof T>(),
    changed: new Set<keyof T>(),
    // Optimistic concurrency is a PLACEHOLDER this round (design §5.6):
    // nothing here ever populates it and no request carries it.
    version: undefined,
    issues: [],
  };
}

/**
 * Record a field edit.
 *
 * `touched` and `changed` are two different sets on purpose: `touched` drives
 * the UI (asterisk, when to validate), `changed` drives the payload and the
 * conflict surface. Only `changed` enters the payload — otherwise editing a
 * value and putting it back still fires a no-op PATCH.
 */
export function setFieldValue<T>(
  node: RecordNode<T>,
  spec: FormFieldSpec,
  value: unknown,
): RecordNode<T> {
  const key = spec.key as keyof T;
  const policy = fieldValuePolicy(spec.type);
  const initialValue = node.initial === null ? undefined : asRecord(node.initial)[spec.key];

  // A record with no server snapshot (create) has nothing to be equal to, so
  // any supplied value counts as changed.
  const same = node.initial !== null && policy.equal(initialValue, value);

  return {
    ...node,
    draft: { ...node.draft, [spec.key]: value } as Draft<T>,
    touched: withKey(node.touched, key),
    changed: same ? withoutKey(node.changed, key) : withKey(node.changed, key),
  };
}

/**
 * Merge a late-arriving server snapshot (a detail fetch that resolved while the
 * operator was already typing). Untouched fields take the server value;
 * touched fields keep the operator's in-flight edit.
 */
export function mergeServerSnapshot<T>(
  node: RecordNode<T>,
  snapshot: Partial<T>,
  specs: readonly FormFieldSpec[],
): RecordNode<T> {
  const incoming = asRecord(snapshot);
  const initial = { ...asRecord(node.initial), ...incoming } as T;
  const draft: Record<string, unknown> = { ...asRecord(node.draft) };

  for (const spec of specs) {
    if (!node.touched.has(spec.key as keyof T) && spec.key in incoming) {
      draft[spec.key] = incoming[spec.key];
    }
  }

  // Re-derive `changed` against the refreshed snapshot: a field the operator
  // edited into the value the server just reported is no longer a change.
  const changed = new Set<keyof T>();
  for (const spec of specs) {
    const key = spec.key as keyof T;
    if (!node.touched.has(key)) continue;
    const policy = fieldValuePolicy(spec.type);
    if (!policy.equal(asRecord(initial)[spec.key], draft[spec.key])) changed.add(key);
  }

  return { ...node, initial, draft: draft as Draft<T>, changed };
}

/**
 * `isDirty` per design §6.2 invariant 2. Relation sub-commands are out of scope
 * this round (no nested write endpoint exists), so the record's own `changed`
 * set is the whole answer — but the shape stays a function so adding pending
 * relation commands later does not become a call-site hunt.
 */
export function isRecordDirty<T>(node: RecordNode<T>): boolean {
  return node.changed.size > 0;
}

function encodeValue(spec: FormFieldSpec, value: unknown): unknown {
  const policy = fieldValuePolicy(spec.type);
  if (!policy.isEmpty(value)) return value;
  // `EmptyEncoding` excludes `undefined` precisely so that `undefined` can
  // mean "no override" without shadowing a legitimate `null` override.
  return spec.encodeEmpty !== undefined ? spec.encodeEmpty : policy.encodeEmpty;
}

/**
 * `update` payload: CHANGED-ONLY.
 *
 * This REVERSES the pre-#1797 behaviour, which echoed back every untouched
 * non-null value. Under `.partial()` semantics an absent key means "do not
 * modify", so echoing back also meant silently last-write-wins overwriting a
 * field somebody else had just changed.
 */
export function buildUpdatePayload<T>(
  node: RecordNode<T>,
  specs: readonly FormFieldSpec[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const draft = asRecord(node.draft);
  for (const spec of specs) {
    if (!node.changed.has(spec.key as keyof T)) continue;
    payload[spec.key] = encodeValue(spec, draft[spec.key]);
  }
  return payload;
}

/**
 * `create` payload: every non-`undefined` draft value. Prefilled values (from an
 * action's context) never reached `changed`, so a changed-only rule here would
 * silently discard the caller's intent.
 */
export function buildCreatePayload<T>(
  node: RecordNode<T>,
  specs: readonly FormFieldSpec[],
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const draft = asRecord(node.draft);
  for (const spec of specs) {
    if (!(spec.key in draft) || draft[spec.key] === undefined) continue;
    payload[spec.key] = encodeValue(spec, draft[spec.key]);
  }
  return payload;
}

/**
 * Commit one successful `inlineSave`. Design §6.2: three things move ATOMICALLY
 * — `initial[field]`, the field's membership in `touched`/`changed`, AND
 * `version`. Updating only the first two leaves the next record-level save
 * carrying a stale token, i.e. a conflict the record picks with itself.
 *
 * `inlineSave` stays default-off this round; this exists so the contract is
 * pinned (and tested) rather than rediscovered later.
 */
export function commitInlineSave<T>(
  node: RecordNode<T>,
  spec: FormFieldSpec,
  savedValue: unknown,
  version?: string,
): RecordNode<T> {
  const key = spec.key as keyof T;
  return {
    ...node,
    initial: { ...asRecord(node.initial), [spec.key]: savedValue } as T,
    touched: withoutKey(node.touched, key),
    changed: withoutKey(node.changed, key),
    version,
    issues: node.issues.filter((issue) => issue.path[0] !== spec.key),
  };
}

export function setIssues<T>(node: RecordNode<T>, issues: Issue<T>[]): RecordNode<T> {
  return { ...node, issues };
}

/** Adopt the server's post-save snapshot: clean slate, same identity. */
export function commitRecordSave<T>(node: RecordNode<T>, saved: T): RecordNode<T> {
  return {
    ...node,
    initial: saved,
    draft: { ...asRecord(saved) } as Draft<T>,
    touched: new Set<keyof T>(),
    changed: new Set<keyof T>(),
    issues: [],
  };
}

/** Drop every uncommitted edit, returning to the server snapshot. */
export function discardEdits<T>(node: RecordNode<T>): RecordNode<T> {
  return {
    ...node,
    draft: { ...asRecord(node.initial) } as Draft<T>,
    touched: new Set<keyof T>(),
    changed: new Set<keyof T>(),
    issues: [],
  };
}
